import {
  ComputeElementStatsRequest,
  ComputeElementStatsResponse,
  QueryCacheOptions,
} from '@fullstory/proto/fs/services/alexa';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { createRequestCancellationAction, RequestDispatch } from '@fullstory/redux-util';
import { createAppCacheActions } from '@fullstory/redux-util/utils/appCache';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { ActionType } from '@fullstory/redux-util/utils/types';
import { isDataFresh } from '@fullstory/searchiecache';
import { getPromiseSettledResultValue } from '@fullstory/util/promise';
import { BatchOptions } from '@fullstory/util/xhr';
import { isEqual } from 'lodash';
import { AppDispatch } from '../../../types';
import {
  ComputeMetricResponseWithId,
  LegacyMetricsRequestBody,
  metricsCacheOptionsRefresh,
  MetricsRequestActions,
} from '../../metrics';
import { SearchCacheKey } from '../../searchMeta';
import { HeatmapsRequestTypes } from '../types';
import { modifyMetricToElementStats } from '../util';
import { UID } from '@fullstory/util/uid';

export const HeatmapsClickAreaActions = createAppCacheActions(
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheIsLoading,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheLoadFailed,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheRefetching,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheHasData,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheFresh,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheSwap,
  HeatmapsRequestTypes.HeatmapsClickAreaAppCacheClear,
)<HeatmapsClickAreaDataType>();

export type HeatmapsClickAreaActions = ActionType<typeof HeatmapsClickAreaActions>;

export interface HeatmapsClickAreaDataType {
  namedElementClickTypeStats: ComputeElementStatsResponse | null | undefined;
  computedAt: timestamp | null;
}

export interface HeatmapsClickAreaPayload {
  cacheOptions: QueryCacheOptions;
  clickType: ComputeElementStatsRequest.StatsType;
  nameElementClickMetricsBody?: LegacyMetricsRequestBody;
  searchCacheKey: SearchCacheKey;
  namedElementIds: string[];
}

export const HeatmapsClickAreaAsyncActions = {
  appCache: (uid: UID, batchOptions?: BatchOptions) => ({
    action: createAppCacheRequestAction<HeatmapsClickAreaDataType, HeatmapsClickAreaPayload>()({
      query: async (
        dispatch: RequestDispatch<HeatmapsClickAreaActions>,
        payload: HeatmapsClickAreaPayload,
        force: boolean,
      ) => {
        const cacheOptions = force ? metricsCacheOptionsRefresh : payload.cacheOptions;

        const namedElementAllClickStatsMetricsPromise = payload.nameElementClickMetricsBody
          ? dispatch(
              MetricsRequestActions.legacyComputeMetric(
                {
                  ...payload.nameElementClickMetricsBody,
                  cacheOptions,
                },
                payload.searchCacheKey,
                batchOptions,
                force,
                uid,
              ),
            )
          : Promise.resolve(undefined);

        let namedElementClickTypeStats: ComputeElementStatsResponse | null = null;
        let computedAt = null;

        await Promise.allSettled([namedElementAllClickStatsMetricsPromise])
          .then(async results => {
            const result: ComputeMetricResponseWithId | undefined = getPromiseSettledResultValue(
              results[0],
            );

            if (result) {
              const clickTypeResult = result;
              computedAt = clickTypeResult.computedAt;

              addNamedElementsWithZeroClicks(clickTypeResult, payload.namedElementIds);

              namedElementClickTypeStats = modifyMetricToElementStats(
                clickTypeResult,
                payload.clickType,
              );
            }
          })
          .catch(e => console.log(e));

        return {
          namedElementClickTypeStats,
          computedAt,
        };
      },
      loading: HeatmapsClickAreaActions.appCacheIsLoading,
      failed: HeatmapsClickAreaActions.appCacheLoadFailed,
      refetching: HeatmapsClickAreaActions.appCacheIsRefetching,
      hasData: HeatmapsClickAreaActions.appCacheHasData,
      fresh: HeatmapsClickAreaActions.appCacheFresh,
      isDataFresh: (payload: HeatmapsClickAreaPayload, data: HeatmapsClickAreaDataType) =>
        isDataFresh(payload.cacheOptions, Timestamp.mustDate(data.computedAt), new Date()),
      isSimiliar: (newData: HeatmapsClickAreaDataType, oldData: HeatmapsClickAreaDataType) => {
        if (!oldData || !newData) {
          return false;
        }
        return isEqual(oldData, newData);
      },
    }),
    cancel: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),
};

/**
 * Adds click stats of 0 clicks for NamedElements which weren't returned in the response.
 */
const addNamedElementsWithZeroClicks = (
  clickTypeResult: ComputeMetricResponseWithId,
  namedElementIds: string[],
) => {
  if (!clickTypeResult?.data?.table) {
    return;
  }

  const uniqueIds = new Set(namedElementIds);

  clickTypeResult.data.table.forEach(resultRow => {
    const groupId = resultRow?.groups[0]?.groupId;
    if (groupId) {
      uniqueIds.delete(groupId);
    }
  });

  uniqueIds.forEach(namedElementId => {
    clickTypeResult!.data!.table.push({
      id: namedElementId,
      startTime: null,
      endTime: null,
      defaultValue: 0,
      groups: [
        {
          fallback: false,
          groupId: namedElementId,
        },
      ],
      value: 0,
      isPartialPeriod: false,
    });
  });
};
